import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { DateRangeChange } from "@/types";
import { DATE_RANGE_KEYS, DEFAULT_QUICK_RANGES, QuickRange, calculateCustomDateRange, calculateDateRange } from "@/utils/dateRangeUtils";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

export interface DateRangeFilterChange {
  key: string;
  startDate: Date;
  endDate: Date;
  previousStartDate: Date;
  previousEndDate: Date;
}

export const DateRangeFilter: React.FC<{
  value: string;
  onChange: (value: DateRangeFilterChange) => void;
  quickRanges?: QuickRange[];
  initialDateRange?: DateRangeChange;
  className?: string;
}> = ({
  value,
  onChange,
  quickRanges = DEFAULT_QUICK_RANGES,
  initialDateRange,
  className
}) => {
    const [open, setOpen] = useState(false);
    const [selectedQuick, setSelectedQuick] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [isSettingFromQuickRange, setIsSettingFromQuickRange] = useState(false);
    const [from, setFrom] = useState<Date | undefined>();
    const [to, setTo] = useState<Date | undefined>();
    const [fromDateError, setFromDateError] = useState<string>("");
    const [toDateError, setToDateError] = useState<string>("");

    const [customTimeRange, setCustomTimeRange] = useState<{ from: Date, to: Date } | null>(null);

    const initialQuick = useRef<string | null>(null);
    const initialCustomFrom = useRef<Date | undefined>(undefined);
    const initialCustomTo = useRef<Date | undefined>(undefined);
    const [appliedTriggerText, setAppliedTriggerText] = useState<string>('Select range');
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Check if current range is a time-based range (weeks, months, quarters, years)
    const isTimeBasedRange = React.useMemo(() => {
      return [
        DATE_RANGE_KEYS.WEEKLY,
        DATE_RANGE_KEYS.MONTHLY,
        DATE_RANGE_KEYS.QUARTERLY,
        DATE_RANGE_KEYS.YEARLY
      ].includes(value as any);
    }, [value]);

    // Calculate minimum date (3 years ago from today)
    const minDate = React.useMemo(() => {
      const today = new Date();
      const threeYearsAgo = new Date(today);
      threeYearsAgo.setFullYear(today.getFullYear() - 3);
      threeYearsAgo.setHours(0, 0, 0, 0);
      return threeYearsAgo;
    }, []);

    // Validate date range
    const validateDateRange = (fromDate: Date | undefined, toDate: Date | undefined) => {
      let hasError = false;

      // Clear previous errors
      setFromDateError("");
      setToDateError("");

      if (fromDate) {
        const normalizedFromDate = new Date(fromDate);
        normalizedFromDate.setHours(0, 0, 0, 0);

        if (normalizedFromDate < minDate) {
          setFromDateError("Select a date within the past 3 years");
          hasError = true;
        }
      }

      if (fromDate && toDate && fromDate > toDate) {
        setFromDateError("From can't be greater than To");
        hasError = true;
      }

      return !hasError;
    };

    // Check if Apply button should be disabled
    const isApplyDisabled = !from || !to || !!fromDateError || !!toDateError;

    // Update applied trigger text when value changes
    useEffect(() => {
      const foundQuickForTrigger = quickRanges.find(q => q.key === value);

      if (foundQuickForTrigger) {
        setAppliedTriggerText(foundQuickForTrigger.label);
      } else {
        const fromDate = from || new Date();
        const toDate = to || new Date();
        setAppliedTriggerText(
          `${format(fromDate, "MMMM d, yyyy")} - ${format(toDate, "MMMM d, yyyy")}`
        );
      }
    }, [value, quickRanges, from, to]);

    useEffect(() => {
      if (initialDateRange?.key === DATE_RANGE_KEYS.CUSTOM) {
        setFrom(new Date(initialDateRange?.startDate));
        setTo(new Date(initialDateRange?.endDate));
      }
    }, [initialDateRange]);

    // Filter quick ranges by search
    const filteredQuickRanges = quickRanges.filter(q => q.label.toLowerCase().includes(search.toLowerCase()));

    // Reset to initial values when opening popover
    const handleOpenChange = (v: boolean) => {
      setOpen(v);
      if (v) {
        const found = quickRanges.find(q => q.key === value);
        setSelectedQuick(found ? found.label : null);
        initialQuick.current = found ? found.label : null;
        setIsSettingFromQuickRange(true);
        // Always set from/to according to the current quick range
        if (found) {
          const dateRange = calculateDateRange(found.key);
          setFrom(dateRange.from);
          setTo(dateRange.to);
          // Reset flag after state updates
          setTimeout(() => setIsSettingFromQuickRange(false), 0);
        } else {
          if (initialDateRange?.key === DATE_RANGE_KEYS.CUSTOM) {
            setFrom(new Date(initialDateRange?.startDate));
            setTo(new Date(initialDateRange?.endDate));
          }
        }
        initialCustomFrom.current = initialDateRange?.key === DATE_RANGE_KEYS.CUSTOM ? new Date(initialDateRange?.startDate) : from;
        initialCustomTo.current = initialDateRange?.key === DATE_RANGE_KEYS.CUSTOM ? new Date(initialDateRange?.endDate) : to;
        setTimeout(() => setIsSettingFromQuickRange(false), 0);
        setSearch("");
        setFromDateError(""); // Clear errors when opening
        setToDateError("");
      }
    };

    // When selecting quick range
    const handleQuickChange = (label: string) => {
      setSelectedQuick(label);
      const found = quickRanges.find(q => q.label === label);
      if (found) {
        // Always set from/to according to the selected quick range
        const dateRange = calculateDateRange(found.key);
        setIsSettingFromQuickRange(true);
        setFrom(dateRange.from);
        setTo(dateRange.to);
        // Reset flag after state updates
        setTimeout(() => setIsSettingFromQuickRange(false), 0);

        // Auto apply when selecting quick range
        const startDate = new Date(format(dateRange.from, "yyyy-MM-dd") + 'T00:00:00');
        const endDate = found.key === DATE_RANGE_KEYS.TODAY ? dateRange.to : new Date(format(dateRange.to, "yyyy-MM-dd") + 'T23:59:59.999');

        onChange({
          key: found.key,
          startDate,
          endDate,
          previousStartDate: dateRange.previousFrom,
          previousEndDate: dateRange.previousTo,
        });

        setOpen(false);
      }
    };

    // When manually changing date inputs
    const handleFromChange = (date: Date | undefined) => {
      if (isSettingFromQuickRange) return;

      setFrom(date);
      setSelectedQuick(null);

      // Always validate the full date range
      validateDateRange(date, to);
    };

    const handleToChange = (date: Date | undefined) => {
      if (isSettingFromQuickRange) return;
      setTo(date);
      setSelectedQuick(null);
      validateDateRange(from, date);
    };

    // Apply
    const handleApply = () => {
      if (!from || !to || !validateDateRange(from, to)) return;

      const startDate = new Date(format(from, "yyyy-MM-dd") + 'T00:00:00');
      const endDate = new Date(format(to, "yyyy-MM-dd") + 'T23:59:59.999');
      const key = "custom";
      const dateRange = calculateCustomDateRange(startDate, endDate);
      setCustomTimeRange({ from: startDate, to: endDate });

      onChange({
        key,
        startDate,
        endDate,
        previousStartDate: dateRange.previousFrom,
        previousEndDate: dateRange.previousTo,
      });

      setOpen(false);
    };

    // Cancel
    const handleCancel = () => {
      setSelectedQuick(initialQuick.current);
      if (initialQuick.current) {
        const found = quickRanges.find(q => q.label === initialQuick.current);
        if (found) {
          const dateRange = calculateDateRange(found.key);
          setIsSettingFromQuickRange(true);
          setFrom(dateRange.from);
          setTo(dateRange.to);
          setTimeout(() => setIsSettingFromQuickRange(false), 0);
        }
      } else {
        setIsSettingFromQuickRange(true);
        setFrom(initialCustomFrom.current);
        setTo(initialCustomTo.current);
        setTimeout(() => setIsSettingFromQuickRange(false), 0);
      }
      setFromDateError(""); // Clear errors when canceling
      setToDateError("");
      setOpen(false);
    };

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-2 w-full min-w-[180px] md:w-[300px]">
            <button
              type="button"
              className={cn("relative flex items-center w-full bg-[#F5F6F7] dark:bg-[#0c3451] rounded-xl px-4 py-1.5 font-bold text-primary dark:text-white text-sm focus:outline-none border-0 shadow-none h-[36px] hover:bg-[#e9eaeb] transition justify-between", className)}
              style={{ boxShadow: "none" }}
            >
              <span className="flex items-center w-full text-left text-sm pr-8">
                <CalendarIcon className="w-4 h-4 mr-2 text-primary dark:text-white flex-shrink-0" />
                <span className="truncate">{appliedTriggerText}</span>
              </span>
              <ChevronDown className="w-4 h-4 text-primary dark:text-white absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </button>
          </div>
        </PopoverTrigger>
        <PopoverContent className={`${isTimeBasedRange ? 'w-[280px]' : 'min-w-[420px]'} p-0 text-sm`} align="start">
          <div className="p-4 pt-2 flex flex-col gap-3 text-sm">
            <div className="flex flex-row gap-4">
              {/* Left: Date inputs */}
              <div className={`flex flex-col gap-2 min-w-[160px] w-[160px] justify-between ${isTimeBasedRange ? 'hidden' : ''}`}>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold mb-0.5 text-primary dark:text-white">From</label>
                  <DatePicker
                    value={from}
                    onValueChange={handleFromChange}
                    placeholder="Select start date"
                    format="yyyy/MM/dd"
                    disabled={isTimeBasedRange}
                    inputClassName="border-primary dark:border-white border-[1.5px] text-primary dark:text-white font-medium bg-white dark:bg-[#3C5C73] text-sm"
                    showClearButton={false}
                  />
                  {fromDateError && (
                    <div className="text-red-500" style={{ fontSize: '0.58rem' }}>{fromDateError}</div>
                  )}
                  <label className="text-xs font-bold mb-0.5 text-primary dark:text-white">To</label>
                  <DatePicker
                    value={to}
                    onValueChange={handleToChange}
                    placeholder="Select end date"
                    format="yyyy/MM/dd"
                    disabled={isTimeBasedRange}
                    inputClassName="border-primary dark:border-white border-[1.5px] text-primary dark:text-white font-medium bg-white dark:bg-[#3C5C73] text-sm"
                    maxDate={new Date()}
                    showClearButton={false}
                  />
                  {toDateError && (
                    <div className="text-red-500" style={{ fontSize: '0.58rem' }}>{toDateError}</div>
                  )}
                </div>
                {!isTimeBasedRange && (
                  <div className="flex gap-2 mt-4">
                    <Button variant="ghost" onClick={handleCancel} className="bg-gray-100 dark:bg-[#2C4C63] border text-gray-700 dark:text-white text-sm">Cancel</Button>
                    <Button
                      onClick={handleApply}
                      disabled={isApplyDisabled}
                      className={`text-sm ${isApplyDisabled ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-action hover:bg-action/90'}`}
                    >
                      Apply
                    </Button>
                  </div>
                )}
              </div>
              {/* Right: Quick ranges */}
              <div className="flex flex-col gap-2 flex-1 min-w-[180px]">
                <label className="text-xs font-bold mb-0.5 text-primary dark:text-white">Quick ranges</label>
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mb-1 border-primary dark:border-white border-[1.5px] text-primary dark:text-white font-medium bg-white dark:bg-[#3C5C73] text-sm"
                />
                <div className="overflow-y-auto max-h-[160px] border border-[#e5e7eb] dark:border-[#2C4C63] rounded-md bg-white dark:bg-[#3C5C73]">
                  {filteredQuickRanges.length === 0 && (
                    <div className="p-2 text-gray-400 dark:text-gray-300 text-xs">No quick ranges</div>
                  )}
                  {filteredQuickRanges.map((q) => (
                    <button
                      key={q.label}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] ${selectedQuick === q.label ? 'bg-[#e9eaeb] dark:bg-[#2C4C63] font-bold' : ''} text-primary dark:text-white`}
                      onClick={() => handleQuickChange(q.label)}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

export default DateRangeFilter;
